import { ArrowLeft } from "lucide-react";
import LanguageSwitcher from "../components/LanguageSwitcher";

const ASSETS = {
  logo: "/logo.png",
};

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="border-b border-white/10 p-6 md:p-8">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-6">
            <a href="/" className="text-white/60 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </a>
            <img src={ASSETS.logo} alt="Gaffer Athletics" className="h-8" />
          </div>
          <LanguageSwitcher />
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 md:px-8 py-12 md:py-16">
        <h1 className="heading-xl text-3xl md:text-4xl text-white mb-8">Privacy & Cookie Policy</h1>
        <p className="text-white/40 text-sm mb-12">Last updated: February 2026</p>

        <div className="space-y-12 body-text text-white/70">
          {/* Introduction */}
          <section>
            <h2 className="heading-md text-xl text-white mb-4">1. Introduction</h2>
            <p>
              At Gaffer Athletics, we are committed to protecting your privacy and ensuring the security of your personal data. This Privacy & Cookie Policy explains how we collect, use, store, and protect your information in compliance with the General Data Protection Regulation (GDPR) and applicable Danish data protection laws.
            </p>
          </section>

          {/* Data Controller */}
          <section>
            <h2 className="heading-md text-xl text-white mb-4">2. Data Controller</h2>
            <p className="mb-4">
              The data controller responsible for your personal data is:
            </p>
            <ul className="list-none space-y-2 text-white/60">
              <li><strong className="text-white">Company Name:</strong> Gaffer Athletics ApS</li>
              <li><strong className="text-white">Address:</strong> 8870 Langå, Denmark</li>
              <li><strong className="text-white">CVR Number:</strong> 46469550</li>
              <li><strong className="text-white">Email:</strong> contact@gafferathletics.com</li>
            </ul>
          </section>

          {/* Data We Collect */}
          <section>
            <h2 className="heading-md text-xl text-white mb-4">3. Personal Data We Collect</h2>
            <p className="mb-4">We may collect the following types of personal data:</p>
            
            <h3 className="heading-md text-lg text-white mt-6 mb-3">3.1 Information You Provide</h3>
            <ul className="list-disc list-inside space-y-2 text-white/60 ml-4">
              <li>Name and contact details (email address, phone number, postal address)</li>
              <li>Payment information (processed securely by our payment providers)</li>
              <li>Order history and preferences</li>
              <li>Communications with our customer service</li>
              <li>Waitlist registration information</li>
            </ul>

            <h3 className="heading-md text-lg text-white mt-6 mb-3">3.2 Information Collected Automatically</h3>
            <ul className="list-disc list-inside space-y-2 text-white/60 ml-4">
              <li>IP address and device information</li>
              <li>Browser type and settings</li>
              <li>Pages visited and interaction data</li>
              <li>Referral source</li>
            </ul>
          </section>

          {/* How We Use Data */}
          <section>
            <h2 className="heading-md text-xl text-white mb-4">4. How We Use Your Data</h2>
            <p className="mb-4">We use your personal data for the following purposes:</p>
            <ul className="list-disc list-inside space-y-2 text-white/60 ml-4">
              <li><strong className="text-white">Order Processing:</strong> To process and fulfill your orders, including delivery and payment</li>
              <li><strong className="text-white">Customer Service:</strong> To respond to your inquiries and provide support</li>
              <li><strong className="text-white">Marketing:</strong> To send you updates about our products (with your consent)</li>
              <li><strong className="text-white">Legal Compliance:</strong> To comply with legal obligations and protect our rights</li>
              <li><strong className="text-white">Website Improvement:</strong> To analyze usage patterns and improve our services</li>
            </ul>
          </section>

          {/* Legal Basis */}
          <section>
            <h2 className="heading-md text-xl text-white mb-4">5. Legal Basis for Processing</h2>
            <p className="mb-4">We process your personal data based on the following legal grounds:</p>
            <ul className="list-disc list-inside space-y-2 text-white/60 ml-4">
              <li><strong className="text-white">Contract:</strong> Processing necessary for performing a contract with you</li>
              <li><strong className="text-white">Consent:</strong> Where you have given explicit consent</li>
              <li><strong className="text-white">Legal Obligation:</strong> Processing necessary for compliance with legal requirements</li>
              <li><strong className="text-white">Legitimate Interest:</strong> Processing necessary for our legitimate business interests</li>
            </ul>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="heading-md text-xl text-white mb-4">6. Data Retention</h2>
            <p>
              We retain your personal data only for as long as necessary to fulfill the purposes for which it was collected, or as required by law. Order data is retained for 5 years for accounting purposes. Marketing data is retained until you withdraw your consent.
            </p>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="heading-md text-xl text-white mb-4">7. Your Rights</h2>
            <p className="mb-4">Under GDPR, you have the following rights:</p>
            <ul className="list-disc list-inside space-y-2 text-white/60 ml-4">
              <li><strong className="text-white">Right of Access:</strong> Request a copy of your personal data</li>
              <li><strong className="text-white">Right to Rectification:</strong> Request correction of inaccurate data</li>
              <li><strong className="text-white">Right to Erasure:</strong> Request deletion of your data ("right to be forgotten")</li>
              <li><strong className="text-white">Right to Restriction:</strong> Request limitation of processing</li>
              <li><strong className="text-white">Right to Data Portability:</strong> Receive your data in a machine-readable format</li>
              <li><strong className="text-white">Right to Object:</strong> Object to processing based on legitimate interests</li>
              <li><strong className="text-white">Right to Withdraw Consent:</strong> Withdraw consent at any time</li>
            </ul>
            <p className="mt-4">
              To exercise any of these rights, please contact us at contact@gafferathletics.com. We will respond within 30 days.
            </p>
          </section>

          {/* Cookies */}
          <section>
            <h2 className="heading-md text-xl text-white mb-4">8. Cookie Policy</h2>
            <p className="mb-4">
              Our website uses cookies and similar technologies. Cookies are small text files stored on your device that help us provide and improve our services.
            </p>

            <h3 className="heading-md text-lg text-white mt-6 mb-3">8.1 Types of Cookies We Use</h3>
            
            <div className="space-y-4 mt-4">
              <div className="p-4 border border-white/10 bg-white/5">
                <h4 className="text-white font-semibold mb-2">Necessary Cookies</h4>
                <p className="text-sm">Essential for the website to function. These cannot be disabled.</p>
              </div>
              
              <div className="p-4 border border-white/10 bg-white/5">
                <h4 className="text-white font-semibold mb-2">Analytics Cookies</h4>
                <p className="text-sm">Help us understand how visitors interact with our website. We use these to improve our services.</p>
              </div>
              
              <div className="p-4 border border-white/10 bg-white/5">
                <h4 className="text-white font-semibold mb-2">Marketing Cookies</h4>
                <p className="text-sm">Used to track visitors across websites to display relevant advertisements.</p>
              </div>
            </div>

            <h3 className="heading-md text-lg text-white mt-6 mb-3">8.2 Managing Cookies</h3>
            <p>
              When you first visit our website, you will be presented with a cookie consent banner. You can choose which categories of cookies to accept. You can also manage cookies through your browser settings at any time.
            </p>
          </section>

          {/* Third Parties */}
          <section>
            <h2 className="heading-md text-xl text-white mb-4">9. Third-Party Services</h2>
            <p className="mb-4">We may share your data with the following third parties:</p>
            <ul className="list-disc list-inside space-y-2 text-white/60 ml-4">
              <li>Payment processors (for secure payment handling)</li>
              <li>Shipping providers (for order delivery)</li>
              <li>Analytics services (for website improvement)</li>
              <li>Email marketing platforms (with your consent)</li>
            </ul>
            <p className="mt-4">
              All third parties are required to protect your data in accordance with GDPR.
            </p>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="heading-md text-xl text-white mb-4">10. Data Security</h2>
            <p>
              We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. This includes encryption, secure servers, and access controls.
            </p>
          </section>

          {/* International Transfers */}
          <section>
            <h2 className="heading-md text-xl text-white mb-4">11. International Data Transfers</h2>
            <p>
              Your data may be transferred to and processed in countries outside the EU/EEA. When this occurs, we ensure appropriate safeguards are in place, such as Standard Contractual Clauses approved by the European Commission.
            </p>
          </section>

          {/* Complaints */}
          <section>
            <h2 className="heading-md text-xl text-white mb-4">12. Complaints</h2>
            <p>
              If you believe we have not handled your personal data properly, you have the right to lodge a complaint with the Danish Data Protection Agency (Datatilsynet) at www.datatilsynet.dk.
            </p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="heading-md text-xl text-white mb-4">13. Contact Us</h2>
            <p>
              For any questions about this Privacy & Cookie Policy, please contact us at:
            </p>
            <p className="mt-4 text-white">
              Email: contact@gafferathletics.com
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 p-6 md:p-8">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-white/30 text-sm">
            © 2026 Gaffer Athletics. Engineering for the Sideline.
          </p>
        </div>
      </footer>
    </div>
  );
}
